import React, { useState } from 'react';
import { Card, Button, Typography, Tag, Table, Modal, Form, Select, Space, Alert, DatePicker, InputNumber } from 'antd';
import { useToast } from '../../../contexts/ToastContext';
import { CrownOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminQueryKeys } from '../../../lib/queryClient';
import TabListLoading from '../../../components/Common/TabListLoading';
import { apiService } from '../../../services/api';
import { AdminSubscriptionResponse, AdminManageSubscriptionRequest, SubscriptionType, UpdateSubscriptionPricingRequest } from '../../../types';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

const AdminSubscriptionTab: React.FC = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [userTypeFilter, setUserTypeFilter] = useState<string>('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<string>('');
  const [manageModalVisible, setManageModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<{ userId: number; userType: string } | null>(null);
  const [selectedSubscription, setSelectedSubscription] = useState<AdminSubscriptionResponse | null>(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [pricingForm] = Form.useForm();

  const { data, isLoading: loading } = useQuery({
    queryKey: adminQueryKeys.subscriptions(page, pageSize, userTypeFilter || undefined, paymentStatusFilter || undefined),
    queryFn: async () => {
      const r = await apiService.getAllSubscriptions(page, pageSize, userTypeFilter || undefined, paymentStatusFilter || undefined);
      if (!r.success) throw new Error(r.message);
      const nestedData = r.data as any;
      const dataArray = Array.isArray(nestedData?.data) ? nestedData.data : Array.isArray(r.data) ? r.data : [];
      const total = nestedData?.totalCount ?? (r as any).totalCount ?? 0;
      return { items: dataArray as AdminSubscriptionResponse[], total };
    },
    staleTime: 1000 * 60 * 2,
  });
  const subscriptions = data?.items ?? [];
  const totalCount = data?.total ?? 0;

  const { data: pricingData, isLoading: pricingLoading } = useQuery({
    queryKey: adminQueryKeys.subscriptionPricing,
    queryFn: async () => {
      const r = await apiService.getAdminSubscriptionPricing();
      if (!r.success) throw new Error(r.message);
      return r.data;
    },
    staleTime: 1000 * 60 * 2,
  });

  const manageMutation = useMutation({
    mutationFn: (request: AdminManageSubscriptionRequest) => apiService.manageUserSubscription(request),
    onSuccess: (res) => {
      if (res.success) {
        setManageModalVisible(false);
        setEditModalVisible(false);
        form.resetFields();
        editForm.resetFields();
        setSelectedUser(null);
        setSelectedSubscription(null);
        queryClient.invalidateQueries({ queryKey: ['admin-panel', 'subscriptions'] });
        toast.success('Abonelik başarıyla yönetildi.');
      } else toast.error(res.message || 'Abonelik yönetilirken bir hata oluştu.');
    },
    onError: () => toast.error('Abonelik yönetilirken bir hata oluştu.'),
  });

  const cancelMutation = useMutation({
    mutationFn: (id: number) => apiService.cancelSubscriptionAdmin(id),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Abonelik iptal edildi.');
        queryClient.invalidateQueries({ queryKey: ['admin-panel', 'subscriptions'] });
      } else toast.error(res.message || 'Abonelik iptal edilirken bir hata oluştu.');
    },
    onError: () => toast.error('Abonelik iptal edilirken bir hata oluştu.'),
  });

  const pricingMutation = useMutation({
    mutationFn: (request: UpdateSubscriptionPricingRequest) => apiService.updateAdminSubscriptionPricing(request),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Abonelik fiyatları güncellendi.');
        queryClient.invalidateQueries({ queryKey: adminQueryKeys.subscriptionPricing });
        queryClient.invalidateQueries({ queryKey: ['subscription-pricing'] });
      } else {
        toast.error(res.message || 'Abonelik fiyatları güncellenirken hata oluştu.');
      }
    },
    onError: () => toast.error('Abonelik fiyatları güncellenirken hata oluştu.'),
  });

  const handleUpdatePricing = (values: any) => {
    pricingMutation.mutate({
      monthlyPrice: values.monthlyPrice,
      yearlyPrice: values.yearlyPrice,
    });
  };

  const handleManage = (userId: number, userType: string) => {
    setSelectedUser({ userId, userType });
    setManageModalVisible(true);
    form.resetFields();
  };

  const handleEdit = (subscription: AdminSubscriptionResponse) => {
    setSelectedSubscription(subscription);
    setSelectedUser({ userId: subscription.userId, userType: subscription.userType });
    editForm.setFieldsValue({
      subscriptionType: subscription.subscriptionType,
      startDate: subscription.startDate ? dayjs(subscription.startDate) : null,
      endDate: subscription.endDate ? dayjs(subscription.endDate) : null,
      amount: subscription.amount
    });
    setEditModalVisible(true);
  };

  const handleEditSubmit = (values: any) => {
    if (!selectedUser) return;
    const request: AdminManageSubscriptionRequest = {
      userId: selectedUser.userId,
      userType: selectedUser.userType,
      subscriptionType: values.subscriptionType,
      startDate: values.startDate ? values.startDate.toISOString() : undefined,
      endDate: values.endDate ? values.endDate.toISOString() : undefined,
      amount: values.amount
    };
    manageMutation.mutate(request);
  };

  const handleManageSubmit = (values: any) => {
    if (!selectedUser) return;
    const request: AdminManageSubscriptionRequest = {
      userId: selectedUser.userId,
      userType: selectedUser.userType,
      subscriptionType: values.subscriptionType,
      startDate: values.startDate ? values.startDate.toISOString() : undefined,
      endDate: values.endDate ? values.endDate.toISOString() : undefined,
      amount: values.amount
    };
    manageMutation.mutate(request);
  };

  const handleCancel = (id: number) => {
    cancelMutation.mutate(id);
  };

  const getSubscriptionTypeName = (type: SubscriptionType): string => {
    switch (type) {
      case SubscriptionType.Free:
        return 'Ücretsiz';
      case SubscriptionType.Monthly:
        return 'Aylık';
      case SubscriptionType.Yearly:
        return 'Yıllık';
      default:
        return 'Bilinmiyor';
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
      responsive: ['md'] as any,
    },
    {
      title: 'Kullanıcı',
      key: 'user',
      width: 180,
      fixed: 'left' as const,
      ellipsis: true,
      render: (_: any, record: AdminSubscriptionResponse) => (
        <div>
          <div><strong>{record.userName || 'Bilinmiyor'}</strong></div>
          <Text type="secondary" style={{ fontSize: '12px' }}>{record.userEmail || '-'}</Text>
        </div>
      )
    },
    {
      title: 'Kullanıcı Tipi',
      dataIndex: 'userType',
      key: 'userType',
      width: 120,
      responsive: ['md'] as any,
      render: (type: string) => (
        <Tag color={type === 'Mentor' ? 'blue' : type === 'Mentee' ? 'green' : 'orange'}>
          {type}
        </Tag>
      )
    },
    {
      title: 'Abonelik Tipi',
      dataIndex: 'subscriptionType',
      key: 'subscriptionType',
      width: 130,
      render: (type: SubscriptionType) => (
        <Tag color={type === SubscriptionType.Yearly ? 'gold' : 'blue'}>
          {getSubscriptionTypeName(type)}
        </Tag>
      )
    },
    {
      title: 'Başlangıç',
      dataIndex: 'startDate',
      key: 'startDate',
      width: 110,
      responsive: ['lg'] as any,
      render: (date: string | null | undefined) => {
        if (!date) return '-';
        try {
          return dayjs(date).format('DD.MM.YYYY');
        } catch {
          return '-';
        }
      }
    },
    {
      title: 'Bitiş',
      dataIndex: 'endDate',
      key: 'endDate',
      width: 110,
      responsive: ['lg'] as any,
      render: (date: string | null | undefined) => {
        if (!date) return '-';
        try {
          return dayjs(date).format('DD.MM.YYYY');
        } catch {
          return '-';
        }
      }
    },
    {
      title: 'Tutar',
      dataIndex: 'amount',
      key: 'amount',
      width: 100,
      render: (amount: number | null | undefined) => {
        if (amount == null) return '-';
        return `₺${amount.toFixed(2)}`;
      }
    },
    {
      title: 'Ödeme Durumu',
      dataIndex: 'paymentStatus',
      key: 'paymentStatus',
      width: 130,
      render: (status: string) => (
        <Tag color={status === 'Completed' ? 'green' : status === 'Failed' ? 'red' : 'orange'}>
          {status === 'Completed' ? 'Tamamlandı' : status === 'Failed' ? 'Başarısız' : 'Beklemede'}
        </Tag>
      )
    },
    {
      title: 'Aktif',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 80,
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'default'}>
          {isActive ? 'Aktif' : 'Pasif'}
        </Tag>
      )
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 180,
      fixed: 'right' as const,
      render: (_: any, record: AdminSubscriptionResponse) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Düzenle
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleManage(record.userId, record.userType)}
          >
            Yeni Abonelik
          </Button>
          {record.isActive && (
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              onClick={() => {
                Modal.confirm({
                  title: 'Aboneliği İptal Et',
                  content: 'Bu aboneliği iptal etmek istediğinizden emin misiniz?',
                  okText: 'Evet',
                  cancelText: 'Hayır',
                  onOk: () => handleCancel(record.id),
                });
              }}
            >
              İptal Et
            </Button>
          )}
        </Space>
      )
    }
  ];

  return (
    <div className="panel-tab-content">
      <div style={{ marginBottom: '24px' }}>
        <Title level={2} style={{ margin: 0, marginBottom: '16px', fontSize: '24px', fontWeight: 600 }}>
          <CrownOutlined style={{ marginRight: '8px' }} />
          Abonelik Yönetimi
        </Title>

        {/* Filtreler */}
        <Card style={{ marginBottom: '16px' }} loading={pricingLoading}>
          <Title level={5} style={{ marginTop: 0 }}>Abonelik Fiyat Ayarları</Title>
          <Form
            form={pricingForm}
            layout="inline"
            initialValues={{
              monthlyPrice: pricingData?.monthlyPrice ?? 5,
              yearlyPrice: pricingData?.yearlyPrice ?? 10,
            }}
            onFinish={handleUpdatePricing}
            key={`${pricingData?.monthlyPrice ?? 5}-${pricingData?.yearlyPrice ?? 10}`}
          >
            <Form.Item
              name="monthlyPrice"
              label="Aylık (₺)"
              rules={[{ required: true, message: 'Zorunlu' }]}
            >
              <InputNumber min={0} precision={2} />
            </Form.Item>
            <Form.Item
              name="yearlyPrice"
              label="Yıllık (₺)"
              rules={[{ required: true, message: 'Zorunlu' }]}
            >
              <InputNumber min={0} precision={2} />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={pricingMutation.isPending}>
                Fiyatları Güncelle
              </Button>
            </Form.Item>
          </Form>
        </Card>

        <Card style={{ marginBottom: '24px' }}>
          <Space size="large">
            <div>
              <Text strong style={{ marginRight: '8px' }}>Kullanıcı Tipi:</Text>
              <Select
                style={{ width: 150 }}
                placeholder="Tümü"
                allowClear
                value={userTypeFilter || undefined}
                onChange={(value) => {
                  setUserTypeFilter(value || '');
                  setPage(1);
                }}
              >
                <Option value="Mentor">Mentor</Option>
                <Option value="Mentee">Mentee</Option>
                <Option value="Company">Company</Option>
              </Select>
            </div>
            <div>
              <Text strong style={{ marginRight: '8px' }}>Ödeme Durumu:</Text>
              <Select
                style={{ width: 150 }}
                placeholder="Tümü"
                allowClear
                value={paymentStatusFilter || undefined}
                onChange={(value) => {
                  setPaymentStatusFilter(value || '');
                  setPage(1);
                }}
              >
                <Option value="Completed">Tamamlandı</Option>
                <Option value="Pending">Beklemede</Option>
                <Option value="Failed">Başarısız</Option>
                <Option value="Refunded">İade Edildi</Option>
              </Select>
            </div>
          </Space>
        </Card>

        {/* Abonelik Listesi */}
        <TabListLoading loading={loading}>
        <Card className="panel-table-card" style={{ borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
          {/* eslint-disable react/jsx-no-duplicate-props */}
          <Table
            scroll={{ x: 'max-content' }}
            columns={columns}
            dataSource={subscriptions}
            rowKey="id"
            loading={loading}
            size="middle"
            pagination={{
              current: page,
              pageSize: pageSize,
              total: totalCount,
              showSizeChanger: true,
              showTotal: (total) => `Toplam ${total} abonelik`,
              onChange: (newPage, newPageSize) => {
                setPage(newPage);
                setPageSize(newPageSize);
              },
              responsive: true,
              simple: window.innerWidth < 768
            }}
          />
          {/* eslint-enable react/jsx-no-duplicate-props */}
        </Card>
        </TabListLoading>

        {/* Yeni Abonelik Modal */}
        <Modal
          title="Yeni Abonelik Oluştur"
          open={manageModalVisible}
          onCancel={() => {
            setManageModalVisible(false);
            form.resetFields();
            setSelectedUser(null);
          }}
          onOk={() => form.submit()}
          width={600}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleManageSubmit}
          >
            <Alert
              message="Kullanıcı Bilgisi"
              description={`Kullanıcı ID: ${selectedUser?.userId}, Tip: ${selectedUser?.userType}`}
              type="info"
              showIcon
              style={{ marginBottom: '16px' }}
            />

            <Form.Item
              name="subscriptionType"
              label="Abonelik Tipi"
              rules={[{ required: true, message: 'Abonelik tipi seçiniz' }]}
            >
              <Select>
                <Option value={SubscriptionType.Free}>Ücretsiz</Option>
                <Option value={SubscriptionType.Monthly}>Aylık</Option>
                <Option value={SubscriptionType.Yearly}>Yıllık</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="startDate"
              label="Başlangıç Tarihi"
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              name="endDate"
              label="Bitiş Tarihi"
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              name="amount"
              label="Tutar (₺)"
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                precision={2}
                placeholder="0.00"
              />
            </Form.Item>
          </Form>
        </Modal>

        {/* Düzenle Modal */}
        <Modal
          title="Abonelik Düzenle"
          open={editModalVisible}
          onCancel={() => {
            setEditModalVisible(false);
            editForm.resetFields();
            setSelectedUser(null);
            setSelectedSubscription(null);
          }}
          onOk={() => editForm.submit()}
          width={600}
        >
          <Form
            form={editForm}
            layout="vertical"
            onFinish={handleEditSubmit}
          >
            <Alert
              message="Abonelik Bilgisi"
              description={`Abonelik ID: ${selectedSubscription?.id}, Kullanıcı: ${selectedSubscription?.userName} (${selectedSubscription?.userType})`}
              type="info"
              showIcon
              style={{ marginBottom: '16px' }}
            />

            <Form.Item
              name="subscriptionType"
              label="Abonelik Tipi"
              rules={[{ required: true, message: 'Abonelik tipi seçiniz' }]}
            >
              <Select>
                <Option value={SubscriptionType.Free}>Ücretsiz</Option>
                <Option value={SubscriptionType.Monthly}>Aylık</Option>
                <Option value={SubscriptionType.Yearly}>Yıllık</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="startDate"
              label="Başlangıç Tarihi"
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              name="endDate"
              label="Bitiş Tarihi"
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              name="amount"
              label="Tutar (₺)"
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                precision={2}
                placeholder="0.00"
              />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default AdminSubscriptionTab;

